import { Controller } from "@hotwired/stimulus"

/**
 * Workspaces::PrivatePersonController
 *
 * Hides the business-only identity inputs (IČO, IČ DPH) of the workspace form
 * while the selected legal form is a private person — the client-side twin of
 * the server's first paint (`private_person` in workspaces/_form and
 * onboarding/workspaces/_form), the same split entity-type-toggle has with the
 * contact modal.
 *
 * The decision is not made here: each <option> of the legal-form select
 * carries `data-private-person`, rendered by workspaces/partials/_legal_form
 * from the accounting, so no legal-form code lives in JavaScript. The
 * VAT-payer, VAT-period and accounting-type blocks are NOT touched here —
 * they live in turbo-frames the navigate refresh re-renders from the server.
 *
 * Why the inputs are hidden rather than removed, and cleared rather than left:
 * legal-entity reaches them by target and contacts--register-diff by id, so
 * they must stay in the DOM; and a hidden input still submits, so the old
 * company's IČO would otherwise ride along until the server dropped it. The
 * cleared value is stashed on the input and put back when the fields return,
 * so a mis-click into "private person" and straight back erases nothing.
 *
 * `selectTargetConnected` (not just the change action) because the select
 * sits inside the `legal_form` turbo-frame, which the navigate refresh
 * replaces on every country or VAT-type change — with a different selection
 * and no change event.
 */
export default class extends Controller {
  static targets = ["select", "businessOnly"]

  selectTargetConnected() {
    this.toggle()
  }

  toggle() {
    const hide = this.#privatePersonSelected()

    this.businessOnlyTargets.forEach((element) => {
      element.classList.toggle("d-none", hide)
      element.querySelectorAll("input").forEach((input) => (hide ? this.#stash(input) : this.#restore(input)))
    })
  }

  #stash(input) {
    if (input.value !== "") input.dataset.privatePersonStash = input.value
    input.value = ""
  }

  #restore(input) {
    if (input.value === "" && input.dataset.privatePersonStash !== undefined) {
      input.value = input.dataset.privatePersonStash
    }
    delete input.dataset.privatePersonStash
  }

  #privatePersonSelected() {
    if (!this.hasSelectTarget) return false

    const option = this.selectTarget.selectedOptions[0]
    return option?.dataset.privatePerson === "true"
  }
}
